import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Camera, Image as ImageIcon, ChevronLeft, ChevronRight, Trash2, Star, Download } from 'lucide-react'
import { getTodoImages, uploadTodoImage, deleteTodoImage, getThumbnailUrl, getOptimizedImageUrl, downloadImage, type TodoImage } from '../lib/imageStorage'
import { supabase } from '../lib/supabase'
import { isDemoModeActive } from '../contexts/DemoContext'
import { Capacitor } from '@capacitor/core'
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera'


interface TodoImageModalProps {
  todoId: string
  todoTitle: string
  isOpen: boolean
  onClose: () => void
  currentThumbnailId?: string | null
  currentThumbnailSize?: 'small' | 'large'
  onSetThumbnail?: (imageId: string | null, size: 'small' | 'large') => void
  /** 指定するとモーダルを開いた直後にこの画像をビューアモードで全画面表示する */
  initialImageId?: string | null
}

interface ImageWithUrl extends TodoImage {
  thumbnailUrl?: string
  optimizedUrl?: string
}

// カメラ/フォトライブラリの権限を確認・リクエストする関数
// 注意: iOS 14以降ではPHPickerを使用するため、写真選択時は権限チェック不要
// getPhoto()を呼ぶと自動的に権限ダイアログが表示される
const requestCameraPermissions = async (source: 'camera' | 'photos'): Promise<boolean> => {
  // iOS 14以降のPHPickerではphotos権限チェックが不要（クラッシュの原因になる）
  // カメラの場合のみ事前チェックを行う
  if (source === 'photos') {
    console.log('[Camera] Skipping permission check for photos (PHPicker handles it)')
    return true
  }

  try {
    // 現在の権限状態を確認
    const permissions = await CapacitorCamera.checkPermissions()
    console.log('[Camera] Current permissions:', permissions)

    const currentPermission = permissions.camera

    // 権限が付与されていない場合はリクエスト
    if (currentPermission !== 'granted') {
      console.log('[Camera] Requesting camera permission...')
      // 引数なしでrequestPermissions()を呼ぶ
      const requested = await CapacitorCamera.requestPermissions()
      console.log('[Camera] Requested permissions result:', requested)

      if (requested.camera !== 'granted') {
        return false
      }
    }

    return true
  } catch (err) {
    console.error('[Camera] Permission check/request error:', err)
    // エラーが発生した場合でも処理を続行（古いOSバージョン対応）
    return true
  }
}

export default function TodoImageModal({ todoId, todoTitle, isOpen, onClose, currentThumbnailId, currentThumbnailSize = 'small', onSetThumbnail, initialImageId }: TodoImageModalProps) {
  const [images, setImages] = useState<ImageWithUrl[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // ローカルステートでサムネイル選択を管理（即座にUIを更新するため）
  const [localThumbnailId, setLocalThumbnailId] = useState<string | null>(currentThumbnailId || null)
  const [localThumbnailSize, setLocalThumbnailSize] = useState<'small' | 'large'>(currentThumbnailSize)

  // Sync local state when modal opens or thumbnail props change
  useEffect(() => {
    if (isOpen) {
      setLocalThumbnailId(currentThumbnailId || null)
      setLocalThumbnailSize(currentThumbnailSize)
    }
  }, [isOpen, currentThumbnailId, currentThumbnailSize])


  // Detect if device is mobile
  useEffect(() => {
    setIsMobile(/Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
  }, [])

  // Load images when modal opens
  useEffect(() => {
    if (isOpen) {
      loadImages(initialImageId ?? undefined)
    } else {
      // 閉じたらビューアモードをリセット（次回開いたときにリストから始まるように）
      setSelectedImageIndex(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, todoId])

  // 他端末での画像追加・削除をリアルタイムに反映（モーダルを開いている間のみ購読）
  useEffect(() => {
    if (!isOpen || isDemoModeActive()) return

    const channel = supabase
      .channel(`todo-images-modal-${todoId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'todo_images', filter: `todo_id=eq.${todoId}` },
        () => {
          loadImages(undefined, true) // silent: 既存表示を残したまま更新
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, todoId])

  const loadImages = async (openImageId?: string, silent = false) => {
    if (!silent) {
      setLoading(true)
    }
    setError(null)
    const result = await getTodoImages(todoId)
    if (result.success && result.images) {
      // Get thumbnail URLs with transform (async with signed URLs)
      const imagesWithUrls = await Promise.all(
        result.images.map(async (img) => ({
          ...img,
          thumbnailUrl: await getThumbnailUrl(img.image_url),
          optimizedUrl: await getOptimizedImageUrl(img.image_url)
        }))
      )
      setImages(imagesWithUrls)
      if (openImageId) {
        // サムネイルクリックから開いた場合：その画像をビューアで直接表示
        const idx = imagesWithUrls.findIndex(img => img.id === openImageId)
        setSelectedImageIndex(idx >= 0 ? idx : imagesWithUrls.length > 0 ? 0 : null)
      } else {
        // 再読込時：ビューア表示中なら選択位置を画像数の範囲内に収める
        setSelectedImageIndex(prev =>
          prev === null || imagesWithUrls.length === 0
            ? null
            : Math.min(prev, imagesWithUrls.length - 1)
        )
      }
    } else {
      setError(result.error || '画像の読み込みに失敗しました')
    }
    if (!silent) {
      setLoading(false)
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // 残りの枠数を計算
    const remainingSlots = 5 - images.length
    const filesToUpload = Array.from(files).slice(0, remainingSlots)

    if (filesToUpload.length === 0) {
      setError('これ以上画像を追加できません（最大5枚）')
      event.target.value = ''
      return
    }

    setUploading(true)
    setError(null)

    let firstUploadedImageId: string | null = null
    let uploadCount = 0
    const errors: string[] = []

    // 順番にアップロード
    for (const file of filesToUpload) {
      const result = await uploadTodoImage(todoId, file)

      if (result.success) {
        uploadCount++
        // 最初にアップロードした画像のIDを記録
        if (!firstUploadedImageId && result.image) {
          firstUploadedImageId = result.image.id
        }
      } else {
        errors.push(result.error || `${file.name}のアップロードに失敗`)
      }
    }

    // 結果を表示
    if (errors.length > 0) {
      setError(`${uploadCount}枚アップロード成功、${errors.length}枚失敗`)
    }

    await loadImages() // Reload images

    // 最初の画像をアップロードした場合、自動的にサムネイルに設定
    if (images.length === 0 && firstUploadedImageId && onSetThumbnail) {
      onSetThumbnail(firstUploadedImageId, 'small')
    }

    setUploading(false)
    // Reset input
    event.target.value = ''
  }

  /**
   * ネイティブカメラで撮影（Capacitorアプリのみ）
   */
  const handleCameraCapture = async () => {
    const isNative = Capacitor.isNativePlatform()

    if (!isNative) {
      // Web版ではこの関数は呼ばれないが、念のため
      console.log('Camera capture is only available on native apps')
      return
    }

    try {
      setUploading(true)
      setError(null)

      // まず権限を確認・リクエスト
      const hasPermission = await requestCameraPermissions('camera')
      if (!hasPermission) {
        setError('カメラへのアクセス許可が必要です。設定アプリから許可してください。')
        return
      }

      // ネイティブカメラで撮影
      const image = await CapacitorCamera.getPhoto({
        quality: 90,
        allowEditing: false, // 編集画面をスキップして高速化
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        saveToGallery: false, // ギャラリーに保存しない（権限問題回避）
      })

      if (!image.webPath) {
        throw new Error('画像のパスを取得できませんでした')
      }

      // 撮影した画像のURIをBlobに変換
      const response = await fetch(image.webPath)
      const blob = await response.blob()

      // ファイル名を生成
      const fileName = `camera_${Date.now()}.${image.format || 'jpg'}`
      const file = new File([blob], fileName, { type: `image/${image.format || 'jpeg'}` })

      // アップロード
      const result = await uploadTodoImage(todoId, file)

      if (result.success) {
        await loadImages() // Reload images

        // 最初の画像をアップロードした場合、自動的にサムネイルに設定
        if (images.length === 0 && result.image && onSetThumbnail) {
          onSetThumbnail(result.image.id, 'small')
        }
      } else {
        setError(result.error || 'アップロードに失敗しました')
      }
    } catch (err: any) {
      console.error('Camera capture error:', err)
      // ユーザーがキャンセルした場合はエラー表示しない
      if (err.message !== 'User cancelled photos app' && !err.message?.includes('cancel')) {
        // 権限エラーの場合は分かりやすいメッセージ
        if (err.message?.includes('permission') || err.message?.includes('denied')) {
          setError('カメラへのアクセス許可が必要です。設定アプリから許可してください。')
        } else {
          setError(`カメラの起動に失敗しました: ${err.message || '不明なエラー'}`)
        }
      }
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (imageId: string) => {
    setDeleteConfirmId(imageId)
  }

  const confirmDelete = async () => {
    if (!deleteConfirmId) return

    // Check if the image being deleted is the current thumbnail
    const isCurrentThumbnail = deleteConfirmId === currentThumbnailId

    const result = await deleteTodoImage(deleteConfirmId)

    if (result.success) {
      // If deleted image was the thumbnail, clear it
      if (isCurrentThumbnail && onSetThumbnail) {
        await onSetThumbnail(null, 'small')
        setLocalThumbnailId(null)
      }

      // If viewing deleted image, close viewer
      if (selectedImageIndex !== null) {
        const deletedImage = images[selectedImageIndex]
        if (deletedImage.id === deleteConfirmId) {
          setSelectedImageIndex(null)
        }
      }
      await loadImages()
    } else {
      setError(result.error || '削除に失敗しました')
    }
    setDeleteConfirmId(null)
  }

  const handlePrevImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1)
    }
  }

  const handleNextImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1)
    }
  }


  if (!isOpen) return null

  const modalContent = selectedImageIndex !== null ? (
    // Image viewer mode - 画面いっぱいに表示
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-95">
      {/* 閉じるボタン */}
      <button
        onClick={() => setSelectedImageIndex(null)}
        className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full z-20"
        aria-label="閉じる"
      >
        <X className="h-6 w-6" />
      </button>

      {/* 左ナビ */}
      {selectedImageIndex > 0 && (
        <button
          onClick={handlePrevImage}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-3 sm:p-2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full z-20"
          aria-label="前の画像"
        >
          <ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8" />
        </button>
      )}

      {/* 右ナビ */}
      {selectedImageIndex < images.length - 1 && (
        <button
          onClick={handleNextImage}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-3 sm:p-2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full z-20"
          aria-label="次の画像"
        >
          <ChevronRight className="h-6 w-6 sm:h-8 sm:w-8" />
        </button>
      )}

      {/* 画像 - 画面全体に表示（下部のコントロール分を除く） */}
      <div
        className="absolute flex items-center justify-center"
        style={{ top: '60px', bottom: '120px', left: '16px', right: '16px' }}
      >
        {images[selectedImageIndex]?.optimizedUrl ? (
          <img
            src={images[selectedImageIndex].optimizedUrl}
            alt={images[selectedImageIndex].file_name}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="text-white">読み込み中...</div>
        )}
      </div>

      {/* 情報・操作エリア - 下部に絶対位置で固定 */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent pt-8 pb-4 px-4">
        <div className="text-white text-center">
          <p className="text-sm truncate">{images[selectedImageIndex]?.file_name}</p>
          <p className="text-xs text-gray-400 mt-1">
            {selectedImageIndex + 1} / {images.length}
          </p>
          <div className="flex gap-2 justify-center mt-3">
            <button
              onClick={async () => {
                try {
                  await downloadImage(
                    images[selectedImageIndex].image_url,
                    images[selectedImageIndex].file_name
                  )
                } catch (err) {
                  setError('ダウンロードに失敗しました')
                }
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              <span>保存</span>
            </button>
            <button
              onClick={() => handleDelete(images[selectedImageIndex].id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>削除</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    // Image list mode
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4 animate-fade-slide-in">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold truncate">{todoTitle} - 画像</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors tap-highlight"
            aria-label="閉じる"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Upload buttons */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex gap-2 flex-wrap">
            {/* Camera button - native camera on mobile apps, HTML capture on web */}
            {isMobile && (
              Capacitor.isNativePlatform() ? (
                // ネイティブアプリ: Capacitor Camera API を使用
                <button
                  onClick={handleCameraCapture}
                  disabled={uploading || images.length >= 5}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                >
                  <Camera className="h-4 w-4" />
                  <span>カメラで撮影</span>
                </button>
              ) : (
                // Web版: HTMLのcapture属性を使用
                <label className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer text-sm">
                  <Camera className="h-4 w-4" />
                  <span>カメラで撮影</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    capture="environment"
                    onChange={handleFileSelect}
                    disabled={uploading || images.length >= 5}
                    className="hidden"
                  />
                </label>
              )
            )}
            {Capacitor.isNativePlatform() ? (
              // ネイティブアプリ: Camera APIでフォトライブラリから選択
              <button
                onClick={async () => {
                  try {
                    setUploading(true)
                    setError(null)

                    // まず権限を確認・リクエスト
                    const hasPermission = await requestCameraPermissions('photos')
                    if (!hasPermission) {
                      setError('写真へのアクセス許可が必要です。設定アプリから許可してください。')
                      return
                    }

                    const image = await CapacitorCamera.getPhoto({
                      quality: 90,
                      allowEditing: false, // 編集画面をスキップして高速化
                      resultType: CameraResultType.Uri,
                      source: CameraSource.Photos, // フォトライブラリから選択
                      presentationStyle: 'popover', // iPadでポップオーバー表示
                    })

                    if (!image.webPath) {
                      throw new Error('画像のパスを取得できませんでした')
                    }

                    const response = await fetch(image.webPath)
                    const blob = await response.blob()
                    const fileName = `photo_${Date.now()}.${image.format || 'jpg'}`
                    const file = new File([blob], fileName, { type: `image/${image.format || 'jpeg'}` })

                    const result = await uploadTodoImage(todoId, file)

                    if (result.success) {
                      await loadImages()

                      // 最初の画像をアップロードした場合、自動的にサムネイルに設定
                      if (images.length === 0 && result.image && onSetThumbnail) {
                        onSetThumbnail(result.image.id, 'small')
                      }
                    } else {
                      setError(result.error || 'アップロードに失敗しました')
                    }
                  } catch (err: any) {
                    console.error('Photo picker error:', err)
                    // ユーザーがキャンセルした場合はエラー表示しない
                    if (err.message !== 'User cancelled photos app' && !err.message?.includes('cancel')) {
                      // デバッグ用: 実際のエラーメッセージを表示
                      setError(`エラー: ${err.message || JSON.stringify(err)}`)
                    }
                  } finally {
                    setUploading(false)
                  }
                }}
                disabled={uploading || images.length >= 5}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
              >
                <ImageIcon className="h-4 w-4" />
                <span>画像を選択</span>
              </button>
            ) : (
              // Web版: HTMLのinput要素（複数選択可）
              <label className={`flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer text-sm ${uploading || images.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <ImageIcon className="h-4 w-4" />
                <span>画像を選択</span>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  multiple
                  onChange={handleFileSelect}
                  disabled={uploading || images.length >= 5}
                  className="hidden"
                />
              </label>
            )}
          </div>
          {images.length >= 5 && (
            <p className="text-xs text-gray-500 mt-2">最大5枚まで添付できます</p>
          )}
          {uploading && (
            <p className="text-xs text-indigo-600 mt-2">アップロード中...</p>
          )}
        </div>

        {/* Image grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">読み込み中...</div>
          ) : images.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ImageIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>画像がありません</p>
              <p className="text-sm mt-1">上のボタンから画像を追加できます</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {images.map((image, index) => (
                <div key={image.id} className="relative group">
                  <button
                    onClick={() => setSelectedImageIndex(index)}
                    className={`w-full aspect-square rounded-lg overflow-hidden border-2 transition-colors bg-gray-100 ${
                      localThumbnailId === image.id
                        ? 'border-yellow-500 ring-2 ring-yellow-300'
                        : 'border-gray-200 hover:border-indigo-500'
                    }`}
                  >
                    {image.thumbnailUrl ? (
                      <img
                        src={image.thumbnailUrl}
                        alt={image.file_name}
                        className="w-full h-full object-cover"
                        style={{ imageRendering: 'crisp-edges' as any }}
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        読込中...
                      </div>
                    )}
                  </button>
                  {/* サムネイル指定ボタン（小サイズ） */}
                  {onSetThumbnail && (
                    <div className="absolute top-2 left-2 flex gap-1">
                      <button
                        onClick={async () => {
                          const newId = localThumbnailId === image.id && localThumbnailSize === 'small' ? null : image.id
                          const newSize = 'small'
                          setLocalThumbnailId(newId)
                          setLocalThumbnailSize(newSize)
                          await onSetThumbnail(newId, newSize)
                        }}
                        style={{
                          backgroundColor: localThumbnailId === image.id && localThumbnailSize === 'small' ? '#eab308' : 'rgba(31, 41, 55, 0.7)',
                          transition: 'background-color 0.3s ease-in-out'
                        }}
                        className="p-1.5 rounded-full text-white hover:bg-yellow-500"
                        title={localThumbnailId === image.id && localThumbnailSize === 'small' ? 'サムネイルを解除' : '小サイズでサムネイルに設定'}
                        aria-label={localThumbnailId === image.id && localThumbnailSize === 'small' ? 'サムネイルを解除' : '小サイズでサムネイルに設定'}
                      >
                        <Star className="h-4 w-4" fill={localThumbnailId === image.id && localThumbnailSize === 'small' ? 'currentColor' : 'none'} />
                      </button>
                      <button
                        onClick={async () => {
                          const newId = localThumbnailId === image.id && localThumbnailSize === 'large' ? null : image.id
                          const newSize = 'large'
                          setLocalThumbnailId(newId)
                          setLocalThumbnailSize(newSize)
                          await onSetThumbnail(newId, newSize)
                        }}
                        style={{
                          backgroundColor: localThumbnailId === image.id && localThumbnailSize === 'large' ? '#eab308' : 'rgba(31, 41, 55, 0.7)',
                          transition: 'background-color 0.3s ease-in-out'
                        }}
                        className="p-1.5 rounded-full text-white hover:bg-yellow-500"
                        title={localThumbnailId === image.id && localThumbnailSize === 'large' ? 'サムネイルを解除' : '大サイズでサムネイルに設定'}
                        aria-label={localThumbnailId === image.id && localThumbnailSize === 'large' ? 'サムネイルを解除' : '大サイズでサムネイルに設定'}
                      >
                        <div className="flex gap-0.5">
                          <Star className="h-4 w-4" fill={localThumbnailId === image.id && localThumbnailSize === 'large' ? 'currentColor' : 'none'} />
                          <Star className="h-4 w-4" fill={localThumbnailId === image.id && localThumbnailSize === 'large' ? 'currentColor' : 'none'} />
                        </div>
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                    aria-label="削除"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-gray-500 mt-1 truncate">{image.file_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // Use portal to render modal at document.body level
  return createPortal(
    <>
      {modalContent}

      {/* 削除確認ダイアログ */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4 animate-fade-slide-in">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full animate-scale-in">
            <p className="text-gray-900 mb-6 font-medium">この画像を削除しますか？</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors tap-highlight font-medium"
              >
                キャンセル
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2.5 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors tap-highlight font-medium"
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  )
}
